import { NotificationList } from './components/notification-list.js';
import { UserRegistration } from './components/user-registration.js';
import { ApiClient } from './services/api-client.js';
import { FCMClient } from './services/fcm-client.js';
import { StorageService } from './services/storage.js';

// API base URL - change this to your worker URL
const API_BASE_URL = 'https://notify-x-worker.optid77.workers.dev';

export class App {
	private storageService: StorageService;
	private apiClient: ApiClient;
	private fcmClient: FCMClient;

	constructor() {
		this.storageService = new StorageService();
		this.apiClient = new ApiClient(API_BASE_URL);
		this.fcmClient = new FCMClient();
	}

	public async init(): Promise<void> {
		this.renderApp();
		await this.initializeFCM();
		this.checkUserRegistration();
	}

	/**
	 * Initialize Firebase Cloud Messaging
	 */
	private async initializeFCM(): Promise<void> {
		try {
			if (this.fcmClient.isSupported()) {
				await this.fcmClient.initialize();
				await this.fcmClient.registerServiceWorker();

				// Set up foreground message handler
				this.fcmClient.setupForegroundMessageHandler((payload) => {
					console.log('Foreground notification received:', payload);

					// Show notification
					this.showForegroundNotification(payload);

					// Refresh notification list if it's visible
					this.refreshNotificationList();
				});
			}
		} catch (error) {
			console.error('Failed to initialize FCM:', error);
		}
	}

	/**
	 * Render main app structure
	 */
	private renderApp(): void {
		const appElement = document.getElementById('app');
		if (!appElement) return;

		appElement.innerHTML = `
			<div class="container">
				<header class="header">
					<h1>Notify-X</h1>
					<p class="subtitle">Bitbucket Notifications</p>
				</header>
				<main id="main-content">
					<div class="loading">Loading...</div>
				</main>
			</div>
		`;
	}

	/**
	 * Check if user is registered
	 */
	private checkUserRegistration(): void {
		const userId = this.storageService.getUserId();
		const username = this.storageService.getUsername();

		if (!userId || !username) {
			this.showRegistrationForm();
		} else {
			this.showNotifications();
		}
	}

	/**
	 * Show registration form
	 */
	private showRegistrationForm(): void {
		const mainContent = document.getElementById('main-content');
		if (!mainContent) return;

		const registration = new UserRegistration(
			this.apiClient,
			this.fcmClient,
			this.storageService,
			() => this.showNotifications(),
		);

		registration.render(mainContent);
	}

	/**
	 * Show notifications list
	 */
	private async showNotifications(): Promise<void> {
		const mainContent = document.getElementById('main-content');
		if (!mainContent) return;

		const notificationList = new NotificationList(this.apiClient, this.storageService);
		await notificationList.render(mainContent);
	}

	/**
	 * Show foreground notification
	 */
	private showForegroundNotification(payload: unknown): void {
		// Extract notification data
		const notification = payload as {
			notification?: { title?: string; body?: string };
		};

		if (notification.notification) {
			const { title, body } = notification.notification;

			// Show browser notification
			if (this.fcmClient.isPermissionGranted()) {
				new Notification(title || 'Notify-X', {
					body: body || '',
					icon: '/icons/icon-192x192.png',
					badge: '/icons/icon-192x192.png',
				});
			}
		}
	}

	/**
	 * Refresh notification list
	 */
	private refreshNotificationList(): void {
		// Trigger a refresh by re-rendering
		const userId = this.storageService.getUserId();
		if (userId) {
			const refreshButton = document.getElementById('refresh-button') as HTMLButtonElement;
			if (refreshButton) {
				refreshButton.click();
			}
		}
	}
}
